/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { useTheme } from 'styled-components';
import { getScanResults, ScanResultRow, ScanResultsData } from '../data';

type HeatmapMetric = 'dns' | 'tcp' | 'curl80' | 'curl443';

const HEATMAP_ROW_ORDER: Array<{ key: HeatmapMetric; label: string }> = [
    { key: 'dns', label: 'dns' },
    { key: 'tcp', label: 'tcp' },
    { key: 'curl80', label: 'curl80' },
    { key: 'curl443', label: 'curl443' },
];

const HEATMAP_MAX_BUCKETS = 96;
const HEATMAP_CELL_H = 26;
const HEATMAP_CELL_W = 16;
const HEATMAP_LABEL_W = 86;
const HEATMAP_TOP_PAD = 12;
const HEATMAP_BOTTOM_PAD = 12;
const HEATMAP_RIGHT_PAD = 12;
const HEATMAP_SUMMARY_GAP = 10;

function clamp01(v: number): number {
    return Math.max(0, Math.min(1, v));
}

function lerp(a: number, b: number, t: number): number {
    return Math.round(a + ((b - a) * t));
}

function rgbToHex(r: number, g: number, b: number): string {
    const p = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
    return `#${p(r)}${p(g)}${p(b)}`;
}

// Keep color scaling behavior consistent with Progress dashboard heatmap.
function heatmapColor(score: number): string {
    const s = Math.pow(clamp01(score), 0.45);
    if (s <= 1 / 3) {
        const t = s / (1 / 3);
        return rgbToHex(lerp(0, 255, t), 0, 0);
    }
    if (s <= 2 / 3) {
        const t = (s - (1 / 3)) / (1 / 3);
        return rgbToHex(255, lerp(0, 212, t), 0);
    }
    const t = (s - (2 / 3)) / (1 / 3);
    return rgbToHex(lerp(255, 57, t), lerp(212, 255, t), lerp(0, 20, t));
}

function formatCsvValue(v: unknown): string {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\r\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function triggerDownload(fileName: string, mime: string, content: string) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function exportRows(rows: ScanResultRow[], format: 'csv' | 'json') {
    const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+$/, '').replace('T', '-');
    if (format === 'json') {
        const content = JSON.stringify(rows, null, 2);
        triggerDownload(`scan-results-${stamp}.json`, 'application/json', content);
        return;
    }

    const header = [
        'finger_id',
        'pvkey',
        'fqdn',
        'ip_address',
        'dns_scan',
        'tcp_scan',
        'curl_scan_80',
        'curl_scan_443',
        'fingerprint_passed',
    ];
    const lines = [header.join(',')];
    for (const row of rows) {
        lines.push([
            row.finger_id,
            row.pvkey,
            row.fqdn,
            row.ip_address,
            row.dns_scan,
            row.tcp_scan,
            row.curl_scan_80,
            row.curl_scan_443,
            row.fingerprint_passed,
        ].map(formatCsvValue).join(','));
    }
    triggerDownload(`scan-results-${stamp}.csv`, 'text/csv;charset=utf-8', lines.join('\n'));
}

function ScanResultsPage() {
    const theme = useTheme() as any;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<ScanResultsData>({
        rows: [],
        rollup: {
            dns: { success: 0, failure: 0 },
            tcp: { success: 0, failure: 0 },
            curl80: { success: 0, failure: 0 },
            curl443: { success: 0, failure: 0 },
            fingerprints: { total: 0, passed: 0, failed: 0 },
            total_rows: 0,
        }
    });
    const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('json');
    const [hoverCell, setHoverCell] = useState<{
        metricLabel: string;
        bucketMin: number | null;
        bucketMax: number | null;
        columnFingerprintTotal: number;
        rowPass: number;
        rowFail: number;
    } | null>(null);

    const panelBorder = `1px solid ${theme?.colors?.ctext || '#6b7280'}`;

    async function refresh() {
        setLoading(true);
        setError(null);
        try {
            const rc = await getScanResults();
            setData(rc);
        } catch (e: any) {
            setError(e?.message || 'Failed to load scan results');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        refresh();
    }, []);

    const heatmap = useMemo(() => {
        const fingerIds = [...new Set(data.rows.map((r) => r.finger_id))].sort((a, b) => a - b);
        if (fingerIds.length === 0) {
            return {
                buckets: [] as Array<{ min: number; max: number; ids: number[] }>,
                counts: {
                    dns: [] as number[],
                    tcp: [] as number[],
                    curl80: [] as number[],
                    curl443: [] as number[],
                },
                maxByMetric: { dns: 0, tcp: 0, curl80: 0, curl443: 0 },
                totalsByMetric: { dns: 0, tcp: 0, curl80: 0, curl443: 0 },
                totalFingerprints: 0,
                svgWidth: HEATMAP_LABEL_W + HEATMAP_RIGHT_PAD,
                svgHeight: HEATMAP_TOP_PAD + HEATMAP_BOTTOM_PAD + (HEATMAP_ROW_ORDER.length * HEATMAP_CELL_H),
            };
        }

        const perFinger = new Map<number, { dns: boolean; tcp: boolean; curl80: boolean; curl443: boolean }>();
        for (const row of data.rows) {
            const prev = perFinger.get(row.finger_id) || { dns: false, tcp: false, curl80: false, curl443: false };
            prev.dns = prev.dns || row.dns_scan === 'success';
            prev.tcp = prev.tcp || row.tcp_scan === 'success';
            prev.curl80 = prev.curl80 || row.curl_scan_80 === 'success';
            prev.curl443 = prev.curl443 || row.curl_scan_443 === 'success';
            perFinger.set(row.finger_id, prev);
        }

        const bucketCount = Math.max(1, Math.min(HEATMAP_MAX_BUCKETS, fingerIds.length));
        const bucketSize = Math.ceil(fingerIds.length / bucketCount);
        const buckets: Array<{ min: number; max: number; ids: number[] }> = [];
        for (let i = 0; i < fingerIds.length; i += bucketSize) {
            const ids = fingerIds.slice(i, i + bucketSize);
            buckets.push({ min: ids[0], max: ids[ids.length - 1], ids });
        }

        const counts = {
            dns: [] as number[],
            tcp: [] as number[],
            curl80: [] as number[],
            curl443: [] as number[],
        };

        for (const b of buckets) {
            let dns = 0;
            let tcp = 0;
            let curl80 = 0;
            let curl443 = 0;
            for (const id of b.ids) {
                const st = perFinger.get(id);
                if (!st) continue;
                if (st.dns) dns++;
                if (st.tcp) tcp++;
                if (st.curl80) curl80++;
                if (st.curl443) curl443++;
            }
            counts.dns.push(dns);
            counts.tcp.push(tcp);
            counts.curl80.push(curl80);
            counts.curl443.push(curl443);
        }

        const maxByMetric = {
            dns: Math.max(0, ...counts.dns),
            tcp: Math.max(0, ...counts.tcp),
            curl80: Math.max(0, ...counts.curl80),
            curl443: Math.max(0, ...counts.curl443),
        };

        const totalsByMetric = {
            dns: counts.dns.reduce((acc, n) => acc + n, 0),
            tcp: counts.tcp.reduce((acc, n) => acc + n, 0),
            curl80: counts.curl80.reduce((acc, n) => acc + n, 0),
            curl443: counts.curl443.reduce((acc, n) => acc + n, 0),
        };

        const totalFingerprints = fingerIds.length;
        const svgWidth = HEATMAP_LABEL_W + HEATMAP_RIGHT_PAD + (buckets.length * HEATMAP_CELL_W) + HEATMAP_SUMMARY_GAP + HEATMAP_CELL_W;
        const svgHeight = HEATMAP_TOP_PAD + HEATMAP_BOTTOM_PAD + (HEATMAP_ROW_ORDER.length * HEATMAP_CELL_H);
        return { buckets, counts, maxByMetric, totalsByMetric, totalFingerprints, svgWidth, svgHeight };
    }, [data.rows]);

    return (
        <Container fluid className="pt-3 px-4">
            <div className="d-flex justify-content-between align-items-center mb-3" style={{ borderBottom: panelBorder, paddingBottom: '0.6rem' }}>
                <div>
                    <h3 className="mb-1">Scan Results</h3>
                    <div className="text-muted">Rollup and detail view across all DNS, TCP, and Curl probes.</div>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Select
                        size="sm"
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json')}
                        style={{ width: '8rem' }}
                    >
                        <option value="csv">CSV</option>
                        <option value="json">JSON</option>
                    </Form.Select>
                    <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => exportRows(data.rows, exportFormat)}
                        disabled={loading || data.rows.length === 0}
                    >
                        Export Details
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={refresh} disabled={loading}>
                        {loading ? 'Loading...' : 'Refresh'}
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading && (
                <Card className="mb-3" style={{ border: panelBorder }}>
                    <Card.Body className="d-flex align-items-center gap-2">
                        <Spinner animation="border" size="sm" />
                        <span>Loading scan results...</span>
                    </Card.Body>
                </Card>
            )}

            {!loading && (
                <>
                    <Row className="g-3 mb-3">
                        <Col md={6} lg={3}>
                            <Card style={{ border: panelBorder }}>
                                <Card.Body>
                                    <div className="small text-muted">DNS</div>
                                    <div><Badge bg="success" className="me-2">{data.rollup.dns.success} success</Badge><Badge bg="danger">{data.rollup.dns.failure} fail</Badge></div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6} lg={3}>
                            <Card style={{ border: panelBorder }}>
                                <Card.Body>
                                    <div className="small text-muted">TCP</div>
                                    <div><Badge bg="success" className="me-2">{data.rollup.tcp.success} success</Badge><Badge bg="danger">{data.rollup.tcp.failure} fail</Badge></div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6} lg={3}>
                            <Card style={{ border: panelBorder }}>
                                <Card.Body>
                                    <div className="small text-muted">Curl 80 / 443</div>
                                    <div><Badge bg="success" className="me-2">{data.rollup.curl80.success + data.rollup.curl443.success} success</Badge><Badge bg="danger">{data.rollup.curl80.failure + data.rollup.curl443.failure} fail</Badge></div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6} lg={3}>
                            <Card style={{ border: panelBorder }}>
                                <Card.Body>
                                    <div className="small text-muted">Fingerprints</div>
                                    <div>
                                        <Badge bg="secondary" className="me-2">{data.rollup.fingerprints.total} total</Badge>
                                        <Badge bg="success" className="me-2">{data.rollup.fingerprints.passed} passed</Badge>
                                        <Badge bg="danger">{data.rollup.fingerprints.failed} failed</Badge>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="mb-3" style={{ border: panelBorder }}>
                        <Card.Header>Fingerprint Heatmap (bucketized)</Card.Header>
                        <Card.Body>
                            {heatmap.buckets.length === 0 ? (
                                <div className="text-muted">No fingerprints in scope.</div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <svg
                                        width={Math.max(heatmap.svgWidth, 1)}
                                        height={heatmap.svgHeight}
                                        viewBox={`0 0 ${Math.max(heatmap.svgWidth, 1)} ${heatmap.svgHeight}`}
                                        style={{ display: 'block' }}
                                    >
                                        {HEATMAP_ROW_ORDER.map((metric, rowIdx) => {
                                            const y = HEATMAP_TOP_PAD + (rowIdx * HEATMAP_CELL_H);
                                            const rowCounts = heatmap.counts[metric.key];
                                            const rowMax = heatmap.maxByMetric[metric.key as HeatmapMetric] || 0;
                                            const rowTotalPass = heatmap.totalsByMetric[metric.key as HeatmapMetric] || 0;
                                            const rowTotalFail = Math.max(0, heatmap.totalFingerprints - rowTotalPass);
                                            return (
                                                <g key={metric.key}>
                                                    <text
                                                        x={HEATMAP_LABEL_W - 8}
                                                        y={y + Math.floor(HEATMAP_CELL_H * 0.65)}
                                                        textAnchor="end"
                                                        fontSize={12}
                                                        fill={theme?.colors?.text || '#e5e7eb'}
                                                    >
                                                        {metric.label}
                                                    </text>
                                                    {heatmap.buckets.map((b, colIdx) => {
                                                        const rowPass = rowCounts[colIdx] || 0;
                                                        const columnFingerprintTotal = b.ids.length;
                                                        const rowFail = Math.max(0, columnFingerprintTotal - rowPass);
                                                        const score = rowMax > 0 ? (rowPass / rowMax) : 0;
                                                        const x = HEATMAP_LABEL_W + (colIdx * HEATMAP_CELL_W);
                                                        return (
                                                            <g key={`${metric.key}-${colIdx}`}>
                                                                <title>{`${metric.label} | ids ${b.min}-${b.max} | fingerprints ${columnFingerprintTotal} | pass ${rowPass} | fail ${rowFail}`}</title>
                                                                <rect
                                                                    x={x}
                                                                    y={y}
                                                                    width={HEATMAP_CELL_W - 1}
                                                                    height={HEATMAP_CELL_H - 2}
                                                                    fill={heatmapColor(score)}
                                                                    stroke="rgba(255,255,255,0.08)"
                                                                    onMouseEnter={() => setHoverCell({
                                                                        metricLabel: metric.label,
                                                                        bucketMin: b.min,
                                                                        bucketMax: b.max,
                                                                        columnFingerprintTotal,
                                                                        rowPass,
                                                                        rowFail,
                                                                    })}
                                                                    onMouseLeave={() => setHoverCell(null)}
                                                                />
                                                            </g>
                                                        );
                                                    })}
                                                    <g>
                                                        <title>{`${metric.label} | all buckets | fingerprints ${heatmap.totalFingerprints} | pass ${rowTotalPass} | fail ${rowTotalFail}`}</title>
                                                        <rect
                                                            x={HEATMAP_LABEL_W + (heatmap.buckets.length * HEATMAP_CELL_W) + HEATMAP_SUMMARY_GAP}
                                                            y={y}
                                                            width={HEATMAP_CELL_W - 1}
                                                            height={HEATMAP_CELL_H - 2}
                                                            fill={heatmapColor(heatmap.totalFingerprints > 0 ? (rowTotalPass / heatmap.totalFingerprints) : 0)}
                                                            stroke="rgba(255,255,255,0.25)"
                                                            onMouseEnter={() => setHoverCell({
                                                                metricLabel: metric.label,
                                                                bucketMin: null,
                                                                bucketMax: null,
                                                                columnFingerprintTotal: heatmap.totalFingerprints,
                                                                rowPass: rowTotalPass,
                                                                rowFail: rowTotalFail,
                                                            })}
                                                            onMouseLeave={() => setHoverCell(null)}
                                                        />
                                                    </g>
                                                </g>
                                            );
                                        })}
                                        <text
                                            x={HEATMAP_LABEL_W + (heatmap.buckets.length * HEATMAP_CELL_W) + HEATMAP_SUMMARY_GAP + Math.floor((HEATMAP_CELL_W - 1) / 2)}
                                            y={HEATMAP_TOP_PAD - 2}
                                            textAnchor="middle"
                                            fontSize={11}
                                            fill={theme?.colors?.textMuted || '#9ca3af'}
                                        >
                                            all
                                        </text>
                                        <text
                                            x={HEATMAP_LABEL_W}
                                            y={heatmap.svgHeight - 2}
                                            fontSize={11}
                                            fill={theme?.colors?.textMuted || '#9ca3af'}
                                        >
                                            fingerprint id buckets ({heatmap.buckets.length} buckets + all summary, {data.rollup.fingerprints.total} fingerprints)
                                        </text>
                                    </svg>
                                    <div
                                        style={{
                                            marginTop: 8,
                                            fontSize: 12,
                                            color: theme?.colors?.textMuted || '#9ca3af',
                                            minHeight: 18,
                                        }}
                                    >
                                        {hoverCell
                                            ? `${hoverCell.bucketMin === null || hoverCell.bucketMax === null ? 'all buckets' : `bucket ids ${hoverCell.bucketMin}-${hoverCell.bucketMax}`} | fingerprints ${hoverCell.columnFingerprintTotal} | ${hoverCell.metricLabel} pass ${hoverCell.rowPass} fail ${hoverCell.rowFail}`
                                            : 'Hover a heatmap cell to see bucket fingerprint total and row pass/fail.'}
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Card style={{ border: panelBorder }}>
                        <Card.Header>Details ({data.rollup.total_rows})</Card.Header>
                        <Card.Body style={{ maxHeight: '55vh', overflow: 'auto' }}>
                            {data.rows.length === 0 ? (
                                <div className="text-muted">No probe rows found.</div>
                            ) : (
                                <Table striped hover bordered size="sm" className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>finger_id</th>
                                            <th>pvkey</th>
                                            <th>fqdn</th>
                                            <th>ip_address</th>
                                            <th>dns_scan</th>
                                            <th>tcp_scan</th>
                                            <th>curl_scan_80</th>
                                            <th>curl_scan_443</th>
                                            <th>fingerprint_passed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.rows.map((row, idx) => (
                                            <tr key={`${row.finger_id}|${row.fqdn}|${row.ip_address || 'null'}|${idx}`}>
                                                <td>{row.finger_id}</td>
                                                <td>{row.pvkey}</td>
                                                <td>{row.fqdn}</td>
                                                <td>{row.ip_address || ''}</td>
                                                <td>{row.dns_scan}</td>
                                                <td>{row.tcp_scan}</td>
                                                <td>{row.curl_scan_80}</td>
                                                <td>{row.curl_scan_443}</td>
                                                <td>{row.fingerprint_passed ? 'true' : 'false'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </>
            )}
        </Container>
    );
}

export default ScanResultsPage;
